import { useState } from 'react'
import { useApolloClient, useMutation, useReactiveVar } from '@apollo/client'
import type { Adapter } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import { GET_ME } from 'api/auth/me'
import { LINK_WALLET } from 'api/wallets/link-wallet'
import { LINK_WALLET_CHALLENGE } from 'api/wallets/link-wallet-challenge'
import {
  connectSelectedSolanaWallet,
  isUserRejectedWalletError,
  signChallengeWithAdapter
} from 'features/auth/utils/connect-solana-adapter'
import type { SolanaConnectorProvider } from 'features/auth/utils/get-solana-connector-providers'
import { usePlayWallet } from 'shared/hooks/use-play-wallet'
import { choosePlayWallet } from 'shared/store/play-wallet'
import { userVar } from 'shared/store/user'
import { isWalletLinkingVar } from '../store/linking'
import {
  notifyAlreadyLinked,
  notifyLinkWalletError,
  notifySignatureDeclined,
  notifyWalletLinked
} from '../utils/notify'

/**
 * Links a Solana wallet to the signed-in account (FE-305, backend DEV-321):
 * `linkWalletChallenge` → the wallet signs the SIWS-style message →
 * `linkWallet` → `me` refetched → the new wallet is selected for play. A
 * wallet that already belongs to another account (including an auto-created
 * Anonymous one) is rejected by the backend and nothing changes.
 */
// eslint-disable-next-line max-statements
export const useLinkWallet = () => {
  const client = useApolloClient()
  const [requestChallenge] = useMutation(LINK_WALLET_CHALLENGE)
  const [commitLinkWallet] = useMutation(LINK_WALLET)
  const { wallet, wallets, select } = useWallet()
  const user = useReactiveVar(userVar)
  const { linkedAddresses, selectWallet } = usePlayWallet()
  const [isLinking, setIsLinking] = useState(false)

  // eslint-disable-next-line max-statements
  const linkAdapterWallet = async (adapter: Adapter, walletAddress: string) => {
    if (linkedAddresses.includes(walletAddress)) {
      selectWallet(walletAddress)
      notifyAlreadyLinked(walletAddress)
      return
    }

    const { data } = await requestChallenge({ variables: { walletAddress } })
    const challenge = data?.linkWalletChallenge.challenge
    if (!challenge) throw new Error('The server returned no message to sign')

    const signature = await signChallengeWithAdapter(adapter, challenge)
    if (!signature) throw new Error('The wallet did not sign the message')

    const { data: linked } = await commitLinkWallet({
      variables: { walletAddress, signature }
    })
    if (!linked || !user) throw new Error('Wallet was not linked')

    // NOTE: the refetched `me` has not reached `linkedAddresses` yet, so the
    // choice is stored directly; the sync keeps it once the wallet is listed
    choosePlayWallet(user.id, linked.linkWallet.address)
    await client.query({ query: GET_ME, fetchPolicy: 'network-only' })
    notifyWalletLinked(linked.linkWallet.address)
  }

  const runLinking = async (link: () => Promise<void>) => {
    if (isLinking) return

    setIsLinking(true)
    isWalletLinkingVar(true)
    try {
      await link()
    } catch (error) {
      if (isUserRejectedWalletError(error)) notifySignatureDeclined()
      else notifyLinkWalletError(error)
    } finally {
      setIsLinking(false)
      isWalletLinkingVar(false)
    }
  }

  /** Links the wallet that is connected right now. */
  const linkConnectedWallet = () =>
    runLinking(async () => {
      const adapter = wallet?.adapter
      const walletAddress = adapter?.publicKey?.toBase58()
      if (!adapter || !walletAddress) throw new Error('Wallet not connected')

      await linkAdapterWallet(adapter, walletAddress)
    })

  /**
   * Re-authorizes the account currently active in the wallet app (so the user
   * can switch accounts there first) and links it.
   */
  const linkProviderWallet = (provider: SolanaConnectorProvider) =>
    runLinking(async () => {
      const connected = await connectSelectedSolanaWallet(
        wallets,
        provider.walletName,
        select
      )

      await linkAdapterWallet(connected.adapter, connected.walletAddress)
    })

  return { linkConnectedWallet, linkProviderWallet, isLinking }
}
